import { Info } from 'lucide-react';
import * as React from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipTrigger,
} from '@/ux-common/components/tooltip/tooltip';
import { cn } from '@/ux-common/lib/utils';

/**
 * InputLabelInfo — an info tooltip trigger placed beside the label. Uses the
 * registry Tooltip. `infoTabStop={false}` removes it from the tab order.
 *
 * Deliberately mounts no `TooltipProvider`. Radix's Provider is app-level policy:
 * it owns the hover delay and the skip-delay group that lets a second tooltip open
 * instantly after the first. One per instance puts every info icon in a group of
 * one and shadows whatever the app configured — a nested Provider silently replaces
 * the app's value with its own default, which is how the legacy `withTooltip` turns
 * an app-wide `delayDuration={0}` back into Radix's 700ms. Consumers must render a
 * `TooltipProvider` at app root; Radix throws without one. See UX-3107.
 */
export interface InputLabelInfoProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  info: React.ReactNode;
  infoTabStop?: boolean;
}
export const InputLabelInfo = React.forwardRef<HTMLButtonElement, InputLabelInfoProps>(
  ({ info, infoTabStop = true, className, ...props }, ref) => (
    <Tooltip>
      <TooltipTrigger
        ref={ref}
        type="button"
        aria-label="More information"
        tabIndex={infoTabStop ? 0 : -1}
        className={cn('input-label-info', className)}
        {...props}
      >
        <Info aria-hidden="true" />
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent>{typeof info === 'string' ? <p>{info}</p> : info}</TooltipContent>
      </TooltipPortal>
    </Tooltip>
  )
);
InputLabelInfo.displayName = 'InputLabelInfo';
